import { motion, AnimatePresence } from "framer-motion";

interface CollagenGraphProps {
  age: string;
  concern: string;
  onNext: () => void;
}

const getUserAge = (ageRange: string): number => {
  const map: Record<string, number> = {
    "25–35": 30, "35–45": 40, "45–55": 50, "55–65": 60, "65–75": 70, "75–85": 80, "85–95": 90,
  };
  return map[ageRange] || 55;
};

const getCollagenAt = (age: number): number => {
  if (age <= 25) return 100;
  if (age <= 50) {
    // 1% per year until 50
    return Math.round(100 - (age - 25) * 1);
  }
  // Accelerates after 50: 2% per year
  const at50 = 75;
  return Math.round(Math.max(10, at50 - (age - 50) * 2));
};

const getConcernText = (concern: string) => {
  const map: Record<string, { symptom: string; worsening: string }> = {
    wrinkles: {
      symptom: "rimpels en fijne lijntjes",
      worsening: "Rimpels worden dieper en vermenigvuldigen zich sneller",
    },
    firmness: {
      symptom: "slappe, hangende huid",
      worsening: "De huid verliest steeds meer stevigheid",
    },
    dullness: {
      symptom: "een doffe, vermoeid ogende teint",
      worsening: "Je huid verliest haar glans en wordt steeds grauwer",
    },
    all: {
      symptom: "rimpels, slapheid én een doffe teint",
      worsening: "Al deze klachten verergeren tegelijk",
    },
  };
  return map[concern] || map["all"];
};

const CollagenGraph = ({ age, concern, onNext }: CollagenGraphProps) => {
  const userAge = getUserAge(age);
  const userCollagen = getCollagenAt(userAge);
  const concernInfo = getConcernText(concern);
  const phase = 3; // Everything renders immediately

  // Chart config — x-axis always goes to 105
  const maxAge = 105;
  const ageRange = maxAge - 25;
  const ageLabels = [];
  for (let a = 25; a <= maxAge; a += 5) ageLabels.push(a);
  const mainAges = ageLabels.filter(a => a <= 95); // main curve only goes to 95

  const W = 420;
  const H = 280;
  const padL = 44;
  const padR = 20;
  const padT = 30;
  const padB = 36;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const toX = (a: number) => padL + ((a - 25) / ageRange) * chartW;
  const toY = (pct: number) => padT + chartH - (pct / 100) * chartH;

  // Main decline curve points
  const points = mainAges.map((a) => ({ x: toX(a), y: toY(getCollagenAt(a)) }));
  const mainPath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  // Future "no action" steeper decline from user age — all the way to 105
  const noActionAges: number[] = [];
  for (let a = userAge; a <= maxAge; a += 2) noActionAges.push(a);
  if (noActionAges[noActionAges.length - 1] !== maxAge) noActionAges.push(maxAge);
  const noActionPoints = noActionAges.map((a) => {
    const yearsFromUser = a - userAge;
    const extraLoss = yearsFromUser * 0.8;
    const pct = Math.max(5, getCollagenAt(a) - extraLoss);
    return { x: toX(a), y: toY(pct), pct };
  });
  const noActionPath = noActionPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  // "With Adelira" — stabilizes and improves, all the way to 105
  const adeliraAges: number[] = [];
  for (let a = userAge; a <= maxAge; a += 2) adeliraAges.push(a);
  if (adeliraAges[adeliraAges.length - 1] !== maxAge) adeliraAges.push(maxAge);
  const adeliraRaw = adeliraAges.map((a) => {
    const yearsFromUser = a - userAge;
    // Slight improvement first 3 years, then very gradual decline (slower than without action)
    let pct: number;
    if (yearsFromUser <= 3) {
      pct = userCollagen + yearsFromUser * 2; // small boost
    } else if (yearsFromUser <= 15) {
      const peak = userCollagen + 6;
      pct = peak - (yearsFromUser - 3) * 0.15; // very gentle decline
    } else {
      const at15 = userCollagen + 6 - 12 * 0.15;
      pct = at15 - (yearsFromUser - 15) * 0.2; // still very slow
    }
    return { age: a, pct: Math.max(10, Math.min(userCollagen + 8, pct)) };
  });
  const adeliraPoints = adeliraRaw.map(p => ({ x: toX(p.age), y: toY(p.pct) }));
  const adeliraPath = adeliraPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  const userX = toX(userAge);
  const userY = toY(userCollagen);

  return (
    <div className="text-center">
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-2xl md:text-4xl font-serif font-medium text-foreground mb-2 leading-tight"
      >
        Jouw collageenverloop
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-foreground/80 font-sans text-lg md:text-xl mb-4 md:mb-8"
      >
        Collageen neemt af vanaf je 25e. Dit is waar jij nu staat
      </motion.p>

      {/* === THE GRAPH === */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="bg-white rounded-2xl shadow-warm border border-border p-3 md:p-4 mb-4 md:mb-6"
      >
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" aria-label="Collageen grafiek">
          {/* Grid lines + Y labels */}
          {[0, 25, 50, 75, 100].map((pct) => (
            <g key={pct}>
              <line
                x1={padL} y1={toY(pct)} x2={W - padR} y2={toY(pct)}
                stroke="hsl(30 15% 90%)" strokeWidth="1"
              />
              <text
                x={padL - 6} y={toY(pct) + 5} textAnchor="end"
                style={{ fontSize: 13, fontFamily: "Inter, sans-serif", fill: "hsl(20 15% 40%)", fontWeight: 500 }}
              >
                {pct}%
              </text>
            </g>
          ))}

          {/* X axis age labels */}
          {ageLabels.filter((_, i) => i % 2 === 0).map((a) => (
            <text
              key={a} x={toX(a)} y={H - 6} textAnchor="middle"
              style={{
                fontSize: a === (Math.round(userAge / 5) * 5) ? 14 : 12,
                fontFamily: "Inter, sans-serif",
                fill: a === (Math.round(userAge / 5) * 5) ? "hsl(350 38% 42%)" : "hsl(20 15% 45%)",
                fontWeight: a === (Math.round(userAge / 5) * 5) ? 700 : 400,
              }}
            >
              {a}
            </text>
          ))}

          {/* Main decline line — animated draw */}
          <motion.path
            d={mainPath}
            fill="none"
            stroke="hsl(30 20% 72%)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="1000"
            strokeDashoffset="1000"
            animate={{ strokeDashoffset: 0 }}
            transition={{ delay: 0.5, duration: 2, ease: "easeOut" }}
          />

          {/* "With Adelira" gold line */}
          <AnimatePresence>
            {phase >= 2 && (
              <motion.path
                d={adeliraPath}
                fill="none"
                stroke="hsl(140 50% 40%)"
                strokeWidth="3.5"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ delay: 0.6, duration: 1.2, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {/* User position dot — pulsing */}
          <motion.circle
            cx={userX} cy={userY} r="8"
            fill="hsl(350 38% 42%)"
            stroke="white" strokeWidth="3"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.8, type: "spring", stiffness: 200 }}
          />

          {/* "Jij nu" label */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
          >
            <rect
              x={userX - 25} y={userY - 34}
              width="50" height="22" rx="6"
              fill="hsl(350 38% 42%)"
            />
            <text
              x={userX} y={userY - 19} textAnchor="middle"
              style={{ fontSize: 13, fontFamily: "Inter, sans-serif", fill: "white", fontWeight: 700 }}
            >
              Jij nu
            </text>
          </motion.g>

        </svg>

        {/* Legend below graph */}
        <AnimatePresence>
          {phase >= 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="flex justify-center gap-6 mt-3 px-2"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block w-5 h-1 rounded-full" style={{ background: "hsl(30 20% 72%)" }} />
                <span className="font-sans text-sm font-semibold" style={{ color: "hsl(20 15% 45%)" }}>Natuurlijk verloop</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-5 h-1 rounded-full" style={{ background: "hsl(140 50% 40%)" }} />
                <span className="font-sans text-sm font-semibold" style={{ color: "hsl(140 50% 30%)" }}>Met behandeling</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Pain point + urgency card */}
      <AnimatePresence>
        {phase >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 md:mb-6"
          >
            <div className="bg-destructive/8 border-2 border-destructive/20 rounded-2xl p-4 md:p-8 text-left">
              <p className="font-serif text-xl md:text-3xl text-foreground mb-2 leading-snug">
                Jouw collageen is nu <span className="text-gradient-gold font-bold">{userCollagen}%</span>
              </p>
              <p className="font-sans text-lg md:text-xl text-foreground/80 mb-3 leading-relaxed">
                Jouw <span className="text-primary font-bold">{concernInfo.symptom}</span> {concernInfo.worsening.toLowerCase()} naarmate dit percentage daalt.
              </p>
              <p className="font-sans text-lg text-destructive font-semibold">
                Zonder actie verlies je elk jaar meer. Dit proces versnelt na je 50e.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <AnimatePresence>
        {phase >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <button onClick={onNext} className="btn-cta">
              Bekijk ons advies voor jouw huid
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollagenGraph;
